import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { axialToWorld, getHexTileMapKey } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import type { Cell } from '@/game/sim';
import { type FocusEntry, focusEntries } from '@/game/view/focusQueue';
import { useFrameStore, useLiveReaders } from './app/simFrame';
import { BadgeBatch, type BadgeSpec } from './badgeBatch';
import { unitWorld } from './HealsLayer';
import { flatRing } from './InstancedMarkers';

const GROUND_Y = config.hexMap.tile.thickness;
// Just above the Send troops ring (AttackTargetMarkers), so the two don't z-fight.
const RING_Y = GROUND_Y + 0.11;
/** Where a line leaves an attacker and lands on a unit target: about the chest. */
const BODY_Y = GROUND_Y + 0.25;
const COLOR = '#f87171';
const LINE_WIDTH = 0.02;
/** Targets ringed and attacker lines drawn at once; more than this aren't drawn. */
const MAX_RINGS = 32;
/** Queued targets' rings: smaller and dimmer than the current ones, which pulse. */
const QUEUED_SCALE = 0.8;
const MAX_LINES = 128;
/** Ring pulses a second, and how far its scale and opacity swing. */
const PULSE_HZ = 1.2;

const ringGeometry = flatRing(0.5, 0.04, 32);
// Boxes point along +z (Object3D.lookAt) and are stretched per instance.
const lineGeometry = new THREE.BoxGeometry(1, 1, 1);
const ringMaterial = new THREE.MeshBasicMaterial({
  color: COLOR,
  transparent: true,
  opacity: 0.9,
  depthWrite: false,
  toneMapped: false,
});
const queuedRingMaterial = new THREE.MeshBasicMaterial({
  color: COLOR,
  transparent: true,
  opacity: 0.5,
  depthWrite: false,
  toneMapped: false,
});
const lineMaterial = new THREE.MeshBasicMaterial({
  color: COLOR,
  transparent: true,
  opacity: 0.45,
  depthWrite: false,
  toneMapped: false,
});

function noRaycast() {}

/** Number badge colours (theme --color-danger, dimmed for queued targets). */
const NUMBER_CURRENT = '#fca5a5';
const NUMBER_QUEUED = '#b98b8b';
/** The badge sits up and to the right of the ring, like the mockup's `.n`, clear of the unit and its health bar. */
const BADGE_DX = 26;
const BADGE_DY = -4;
const BADGE_SIZE = 16;
const BADGE_Y = GROUND_Y + 0.35;

/**
 * The selection's focus: a pulsing red ring on whatever the selected own
 * units are attacking, a thin line from each attacker to it (following the
 * drawn units between ticks), and dimmer rings for the targets queued behind
 * (`focusEntries`), every one numbered by a badge (`BadgeBatch`). Fog-safe: a
 * rival target is marked only while its tile (any tile of a building's
 * footprint) is in sight. Fixed instanced pools (nothing allocated per
 * frame), not raycastable.
 */
export function FocusMarker({ selectedUnitIds }: { selectedUnitIds: readonly string[] }) {
  const { getState, getTileVisibility } = useLiveReaders();
  const store = useFrameStore();
  const size = useThree((s) => s.size);
  const ringRef = useRef<THREE.InstancedMesh>(null);
  const queuedRef = useRef<THREE.InstancedMesh>(null);
  const lineRef = useRef<THREE.InstancedMesh>(null);
  const badges = useMemo(() => new BadgeBatch(), []);
  useEffect(() => () => badges.dispose(), [badges]);
  const scratch = useMemo(
    () => ({ obj: new THREE.Object3D(), from: new THREE.Vector3(), to: new THREE.Vector3() }),
    []
  );
  // Targets change only with a tick or the selection.
  const cache = useRef<{ tick: number; ids: readonly string[]; entries: FocusEntry[] }>({
    tick: -1,
    ids: [],
    entries: [],
  });

  useFrame(({ clock }) => {
    const ring = ringRef.current;
    const queued = queuedRef.current;
    const line = lineRef.current;
    if (!ring || !queued || !line) return;
    const state = getState();
    const { obj, from, to } = scratch;
    const playerId = store.get().playerId;
    const seen = (cell: Cell) => getTileVisibility(getHexTileMapKey(cell)) === 'visible';
    const cached = cache.current;
    const changed = cached.tick !== state.tick || cached.ids !== selectedUnitIds;
    if (changed) {
      cached.tick = state.tick;
      cached.ids = selectedUnitIds;
      cached.entries = focusEntries(state, selectedUnitIds, playerId);
    }
    const wave = 0.5 + 0.5 * Math.sin(clock.elapsedTime * PULSE_HZ * Math.PI * 2);
    ringMaterial.opacity = 0.55 + 0.4 * wave;
    let rings = 0;
    let queuedRings = 0;
    let lines = 0;
    const specs: BadgeSpec[] = [];
    for (const entry of cached.entries) {
      if (rings + queuedRings >= MAX_RINGS) break;
      const unit = state.units[entry.targetId];
      if (unit) {
        if (unit.owner !== playerId && !seen(unit.pos)) continue;
        unitWorld(unit, BODY_Y, to);
      } else {
        const building = state.buildings[entry.targetId];
        if (!building) continue;
        const hidden = !seen(building.pos) && !building.footprint?.some(seen);
        if (building.owner !== playerId && hidden) continue;
        const world = axialToWorld(building.pos, config.hexMap.tile.radius);
        to.set(world.x, BODY_Y, world.z);
      }
      obj.rotation.set(0, 0, 0);
      obj.position.set(to.x, RING_Y, to.z);
      if (entry.current) {
        obj.scale.setScalar(0.9 + 0.2 * wave);
        obj.updateMatrix();
        ring.setMatrixAt(rings++, obj.matrix);
        for (const id of entry.unitIds) {
          const attacker = state.units[id];
          if (!attacker || lines >= MAX_LINES || attacker.order?.kind !== 'attack') continue;
          if (attacker.order.targetId !== entry.targetId) continue;
          unitWorld(attacker, BODY_Y, from);
          const length = from.distanceTo(to);
          if (length < 1e-4) continue;
          obj.position.lerpVectors(from, to, 0.5);
          obj.lookAt(to);
          obj.scale.set(LINE_WIDTH, LINE_WIDTH, length);
          obj.updateMatrix();
          line.setMatrixAt(lines++, obj.matrix);
        }
      } else {
        obj.scale.setScalar(QUEUED_SCALE);
        obj.updateMatrix();
        queued.setMatrixAt(queuedRings++, obj.matrix);
      }
      if (changed) {
        const tint = entry.current ? NUMBER_CURRENT : NUMBER_QUEUED;
        const label = String(entry.index);
        specs.push({
          key: entry.targetId,
          x: to.x,
          y: BADGE_Y,
          z: to.z,
          quads: [
            {
              kind: 'pill',
              x: BADGE_DX,
              y: BADGE_DY,
              w: BADGE_SIZE,
              h: BADGE_SIZE,
              border: true,
              bob: false,
            },
            {
              kind: 'glyph',
              x: BADGE_DX,
              y: BADGE_DY,
              w: badges.measure(label, 11, true),
              h: 14,
              glyph: label,
              font: 11,
              bold: true,
              tint,
              bob: false,
            },
          ],
        });
      }
      // Between ticks only a moving target's badge follows it.
      if (!changed) badges.setAnchor(entry.targetId, to.x, BADGE_Y, to.z);
    }
    // Laid out when the targets or the selection change (visibility moves
    // with a tick, so a target going out of sight is caught here too).
    if (changed) badges.setBadges(specs);
    badges.frame(size.width, size.height, clock.elapsedTime);
    ring.count = rings;
    ring.instanceMatrix.needsUpdate = true;
    queued.count = queuedRings;
    queued.instanceMatrix.needsUpdate = true;
    line.count = lines;
    line.instanceMatrix.needsUpdate = true;
  });

  const pool =
    (target: { current: THREE.InstancedMesh | null }) => (mesh: THREE.InstancedMesh | null) => {
      if (mesh) {
        mesh.count = 0;
        mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      }
      target.current = mesh;
    };

  return (
    <>
      <instancedMesh
        ref={pool(ringRef)}
        args={[ringGeometry, ringMaterial, MAX_RINGS]}
        frustumCulled={false}
        raycast={noRaycast}
      />
      <instancedMesh
        ref={pool(queuedRef)}
        args={[ringGeometry, queuedRingMaterial, MAX_RINGS]}
        frustumCulled={false}
        raycast={noRaycast}
      />
      <instancedMesh
        ref={pool(lineRef)}
        args={[lineGeometry, lineMaterial, MAX_LINES]}
        frustumCulled={false}
        raycast={noRaycast}
      />
      <primitive object={badges.mesh} />
    </>
  );
}
